import React, { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { format, startOfMonth, endOfMonth, subMonths, subDays, startOfYear } from 'date-fns'
import { gql } from '~/__generated__'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import { Switch } from '~/ui/switch'
import { Label } from '~/ui/label'
import { Alert, AlertDescription, AlertTitle } from '~/ui/alert'
import { Badge } from '~/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/ui/table'
import { Skeleton } from '~/ui/skeleton'
import {
  BadgeDollarSign,
  CalendarDays,
  CircleAlert,
  Info,
  Layers3,
  MapPin,
  ReceiptText,
} from 'lucide-react'

const ROYALTY_REPORT_QUERY = gql(`
  query RoyaltyReport(
    $organizationId: ID
    $zoneId: ID
    $locationId: ID
    $startDate: ISO8601Date!
    $endDate: ISO8601Date!
    $includePartial: Boolean
  ) {
    royaltyReporting(
      organizationId: $organizationId
      zoneId: $zoneId
      locationId: $locationId
      startDate: $startDate
      endDate: $endDate
      includePartial: $includePartial
    ) {
      startAt
      endAt
      includePartial
      overallTotals {
        totalPlays
        qualifiedPlays
        partialPlays
        totalDurationMs
        qualifiedDurationMs
      }
      proTotals {
        licensingPro
        totalPlays
        qualifiedPlays
        partialPlays
        totalDurationMs
        qualifiedDurationMs
        zonesReporting
        locationsReporting
      }
      rows {
        organizationId
        locationId
        locationName
        zoneId
        zoneName
        licensingPro
        totalPlays
        qualifiedPlays
        partialPlays
        totalDurationMs
        qualifiedDurationMs
      }
    }
  }
`)

type PeriodPreset = 'this_month' | 'last_month' | '90_days' | 'year_to_date'

const PERIOD_OPTIONS: Array<{ value: PeriodPreset; label: string }> = [
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: '90_days', label: 'Last 90 days' },
  { value: 'year_to_date', label: 'Year to date' },
]

interface RoyaltyReportingDashboardProps {
  organizationId?: string
  zoneId?: string
  locationId?: string
  zoneName?: string
}

const numberFormatter = new Intl.NumberFormat()

const formatDuration = (ms?: number | null) => {
  if (!ms) return '0m'
  const totalMinutes = ms / 1000 / 60
  if (totalMinutes < 60) {
    return `${Math.round(totalMinutes)}m`
  }
  const hours = totalMinutes / 60
  if (hours >= 10) {
    return `${hours.toFixed(0)}h`
  }
  return `${hours.toFixed(1)}h`
}

const computeDateRange = (preset: PeriodPreset) => {
  const now = new Date()

  switch (preset) {
    case 'this_month':
      return { start: startOfMonth(now), end: now }
    case 'last_month': {
      const previous = subMonths(now, 1)
      return { start: startOfMonth(previous), end: endOfMonth(previous) }
    }
    case '90_days':
      return { start: subDays(now, 89), end: now }
    case 'year_to_date':
      return { start: startOfYear(now), end: now }
    default:
      return { start: startOfMonth(now), end: now }
  }
}

export const RoyaltyReportingDashboard: React.FC<RoyaltyReportingDashboardProps> = ({
  organizationId,
  zoneId,
  locationId,
  zoneName,
}) => {
  const [period, setPeriod] = useState<PeriodPreset>('this_month')
  const [includePartial, setIncludePartial] = useState(false)

  const dateRange = useMemo(() => computeDateRange(period), [period])
  const startDate = format(dateRange.start, 'yyyy-MM-dd')
  const endDate = format(dateRange.end, 'yyyy-MM-dd')

  const variables = useMemo(
    () => ({
      organizationId,
      zoneId: zoneId ?? null,
      locationId: locationId ?? null,
      startDate,
      endDate,
      includePartial,
    }),
    [organizationId, zoneId, locationId, startDate, endDate, includePartial]
  )

  const { data, loading, error } = useQuery(ROYALTY_REPORT_QUERY, {
    variables,
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
  })

  const report = data?.royaltyReporting
  const totals = report?.overallTotals
  const proTotals = report?.proTotals ?? []
  const rows = report?.rows ?? []

  const difference = totals ? totals.totalPlays - totals.qualifiedPlays : 0
  const dateLabel = useMemo(() => {
    const startLabel = format(dateRange.start, 'MMM d, yyyy')
    const endLabel = format(dateRange.end, 'MMM d, yyyy')
    return `${startLabel} – ${endLabel}`
  }, [dateRange])

  if (!organizationId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Royalty reporting is available once an organization is selected</CardTitle>
          <CardDescription>
            Select an organization to evaluate PRO-ready playback totals.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  if (loading && !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BadgeDollarSign className="h-5 w-5" />
            Loading royalty reporting
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <CircleAlert className="h-4 w-4" />
        <AlertTitle>We could not load the royalty report</AlertTitle>
        <AlertDescription>
          {error.message || 'An unexpected error occurred while fetching the royalty totals.'}
        </AlertDescription>
      </Alert>
    )
  }

  if (!report) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BadgeDollarSign className="h-5 w-5" />
            Royalty Reporting
          </CardTitle>
          <CardDescription>
            No royalty-qualified playback activity was found for the selected window.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const formattedTotals = {
    totalPlays: numberFormatter.format(totals?.totalPlays ?? 0),
    qualifiedPlays: numberFormatter.format(totals?.qualifiedPlays ?? 0),
    partialPlays: numberFormatter.format(totals?.partialPlays ?? 0),
    qualifiedDuration: formatDuration(totals?.qualifiedDurationMs ?? 0),
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <BadgeDollarSign className="h-5 w-5 text-emerald-600" />
                Royalty Reporting
              </CardTitle>
              <CardDescription>
                These totals are the royalty-qualified plays we remit to performing rights
                organizations.
              </CardDescription>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge variant="outline" className="flex items-center gap-2 text-xs">
                <CalendarDays className="h-3.5 w-3.5" />
                {dateLabel}
              </Badge>
              {zoneName && (
                <Badge variant="secondary" className="flex items-center gap-2 text-xs">
                  <MapPin className="h-3.5 w-3.5" />
                  Filtering to {zoneName}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <Label htmlFor="royalty-period" className="text-sm font-medium">
                Reporting period
              </Label>
              <Select value={period} onValueChange={(value: PeriodPreset) => setPeriod(value)}>
                <SelectTrigger id="royalty-period" className="min-w-[12rem]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="include-partial"
                checked={includePartial}
                onCheckedChange={setIncludePartial}
              />
              <Label htmlFor="include-partial" className="text-muted-foreground text-sm">
                Include partial (non-payable) plays for diagnostic context
              </Label>
            </div>
            <Badge variant="secondary" className="flex items-center gap-2 text-xs">
              <ReceiptText className="h-3.5 w-3.5" />
              Payable dataset
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Alert>
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle>Royalty-ready vs. analytics events</AlertTitle>
        <AlertDescription>
          <p>
            Our analytics dashboards focus on raw playback events for operational visibility. This
            royalty report filters that stream down to the qualified plays that we actually report
            and pay to PROs.
          </p>
          {difference > 0 && (
            <p className="text-muted-foreground text-sm">
              {numberFormatter.format(difference)} event{difference === 1 ? '' : 's'} were excluded
              because they did not meet royalty qualification (usually partial or short plays).
            </p>
          )}
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-emerald-100">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-emerald-700">
              <BadgeDollarSign className="h-4 w-4" /> Qualified plays
            </CardTitle>
            <CardDescription>Plays we remit to PROs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold text-emerald-700">
              {formattedTotals.qualifiedPlays}
            </div>
            <p className="text-muted-foreground text-xs">
              Qualified listening time: {formattedTotals.qualifiedDuration}
            </p>
          </CardContent>
        </Card>

        <Card className="border-amber-100">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-amber-700">
              <Layers3 className="h-4 w-4" /> Total tracked plays
            </CardTitle>
            <CardDescription>All plays captured during the reporting window</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold text-amber-700">
              {formattedTotals.totalPlays}
            </div>
            <p className="text-muted-foreground text-xs">
              Difference vs. payable: {numberFormatter.format(Math.max(difference, 0))}
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <CircleAlert className="h-4 w-4" /> Partial / diagnostic plays
            </CardTitle>
            <CardDescription>Only shown when partial plays are included</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold text-slate-700">
              {formattedTotals.partialPlays}
            </div>
            {!includePartial && (
              <p className="text-muted-foreground text-xs">
                Toggle partial plays on to review excluded events.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ReceiptText className="h-5 w-5 text-emerald-600" />
            PRO payout breakdown
          </CardTitle>
          <CardDescription>Qualified plays by performing rights organization.</CardDescription>
        </CardHeader>
        <CardContent>
          {proTotals.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No PRO activity recorded for this period.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Performing Rights Organization</TableHead>
                  <TableHead className="text-right">Qualified plays</TableHead>
                  <TableHead className="text-right">Partial plays</TableHead>
                  <TableHead className="text-right">Zones reporting</TableHead>
                  <TableHead className="text-right">Locations reporting</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {proTotals.map((pro) => (
                  <TableRow key={pro.licensingPro ?? 'unassigned'}>
                    <TableCell className="font-medium">
                      {pro.licensingPro ?? 'Unassigned'}
                    </TableCell>
                    <TableCell className="text-right">
                      {numberFormatter.format(pro.qualifiedPlays ?? 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      {numberFormatter.format(pro.partialPlays ?? 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      {numberFormatter.format(pro.zonesReporting ?? 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      {numberFormatter.format(pro.locationsReporting ?? 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5 text-blue-600" />
            Zone-level detail
          </CardTitle>
          <CardDescription>Review payable plays by zone and PRO.</CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No royalty-qualified plays were found for this scope.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>PRO</TableHead>
                  <TableHead className="text-right">Qualified plays</TableHead>
                  <TableHead className="text-right">Partial plays</TableHead>
                  <TableHead className="text-right">Qualified listening time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={`${row.zoneId}-${row.licensingPro ?? 'unassigned'}`}>
                    <TableCell>{row.locationName ?? 'Unknown location'}</TableCell>
                    <TableCell>{row.zoneName ?? 'Unknown zone'}</TableCell>
                    <TableCell>{row.licensingPro ?? 'Unassigned'}</TableCell>
                    <TableCell className="text-right">
                      {numberFormatter.format(row.qualifiedPlays ?? 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      {numberFormatter.format(row.partialPlays ?? 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatDuration(row.qualifiedDurationMs)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
